import { Button, CircularProgress, Stack, TextField, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { ReactComponent as SmileyGraphic } from 'assets/onboarding/smiley.svg'
import JelledModal from 'components/JelledModal'
import { COLORS } from 'app-constants'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { ReactComponent as ManagerIcon } from 'assets/onboarding/manager.svg'
import { ReactComponent as IndividualContributorIcon } from 'assets/onboarding/ic.svg'
import { ReactComponent as ExecutiveIcon } from 'assets/onboarding/executive.svg'
import { ReactComponent as InvestorIcon } from 'assets/onboarding/investor.svg'
import { ReactComponent as EngineeringIcon } from 'assets/onboarding/engineering.svg'
import { ReactComponent as CustomerServiceIcon } from 'assets/onboarding/customer_service.svg'
import { ReactComponent as MarketingIcon } from 'assets/onboarding/marketing.svg'
import { ReactComponent as OperationsIcon } from 'assets/onboarding/operations.svg'
import { ReactComponent as SalesIcon } from 'assets/onboarding/sales.svg'
import { ReactComponent as ProductManagementIcon } from 'assets/onboarding/product.svg'
import { ReactComponent as OtherIcon } from 'assets/onboarding/other.svg'
import { OnboardingStep, UserRole } from 'api/model'
import { capitalizeOnlyFirstLetter, formatLabelForDisplay } from 'utils'
import { useActiveOrganization } from 'hooks/use-organization'
import {
  twinsUpdateInsightCategories,
  usersUpdateAuthenticated,
  usersUpdateTwinResponder,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { authenticatedUserAtom, toastAtom } from 'state'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useFetchInsightCategories, useFetchSuggestedCategories } from 'hooks/use-insights'
import { myTwinAtom } from 'state/my-twin'
import { TopicColorItem } from './Onboarding'
import { CheckboxWithDarkMode } from 'dark-mode-components'
import { useFetchTwinResponder } from 'hooks/use-email-responder'

const USER_ROLES = [
  {
    systemName: 'ic',
    displayName: 'Individual contributor',
    icon: <IndividualContributorIcon />,
  },
  {
    systemName: 'manager',
    displayName: 'Manager',
    icon: <ManagerIcon />,
  },
  {
    systemName: 'executive',
    displayName: 'Executive (C-level or VP)',
    icon: <ExecutiveIcon />,
  },
  {
    systemName: 'investor',
    displayName: 'Investor',
    icon: <InvestorIcon />,
  },
]

export const UserRoleList = ({
  selectedRole,
  setSelectedRole,
}: {
  selectedRole: UserRole | null
  setSelectedRole: React.Dispatch<React.SetStateAction<UserRole | null>>
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack spacing={1} justifyContent="flex-start" width="100%" alignSelf="center">
      {USER_ROLES.map((role, index) => (
        <Stack
          key={index}
          borderRadius="8px"
          width={isMobile ? 'calc(100% - 32px)' : '100%'}
          p={2}
          direction="row"
          spacing="10px"
          alignItems="center"
          border={`1px solid ${COLORS.GRAY_5}`}
          onClick={() => setSelectedRole(role.systemName as UserRole)}
          sx={{
            cursor: 'pointer',
            background: selectedRole === role.systemName ? COLORS.JELLED_MID_BLUE_10_PERCENT : 'transparent',
            border:
              selectedRole === role.systemName
                ? `1px solid ${COLORS.JELLED_PRIMARY_BLUE}`
                : `1px solid ${COLORS.GRAY_5}`,
            '&:hover': {
              border: `1px solid ${COLORS.JELLED_PRIMARY_BLUE}`,
              background: COLORS.JELLED_MID_BLUE_10_PERCENT,
            },
          }}>
          {role.icon}
          <Typography variant="h5">{role.displayName}</Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export const FUNCTIONS = [
  {
    text: 'engineering',
    icon: <EngineeringIcon />,
  },
  {
    text: 'operations',
    icon: <OperationsIcon />,
  },
  {
    text: 'product',
    icon: <ProductManagementIcon />,
  },
  {
    text: 'customer_service',
    icon: <CustomerServiceIcon />,
  },
  {
    text: 'sales',
    icon: <SalesIcon />,
  },
  {
    text: 'marketing',
    icon: <MarketingIcon />,
  },
  {
    text: 'other',
    icon: <OtherIcon />,
  },
]

export const FunctionsList = ({
  selectedFunction,
  setSelectedFunction,
  setCustomFunction,
}: {
  selectedFunction: string
  setSelectedFunction: React.Dispatch<React.SetStateAction<string>>
  setCustomFunction: React.Dispatch<React.SetStateAction<string>>
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack
      flexWrap="wrap"
      gap="8px"
      direction="row"
      justifyContent="flex-start"
      width={isMobile ? '100%' : '608px'}
      alignSelf="center">
      {FUNCTIONS.map((f, index) => (
        <Stack
          key={index}
          borderRadius="8px"
          width={isMobile ? 'calc(100% - 32px)' : '300px'}
          p={2}
          direction="row"
          spacing="10px"
          alignItems="center"
          border={`1px solid ${COLORS.GRAY_5}`}
          onClick={() => {
            if (selectedFunction === f.text) {
              setSelectedFunction('')
              return
            }

            // if work type is not other, we want to clear the other setting
            if (f.text !== 'other') {
              setCustomFunction('')
            }
            setSelectedFunction(f.text)
          }}
          sx={{
            cursor: 'pointer',
            background: selectedFunction === f.text ? COLORS.JELLED_MID_BLUE_10_PERCENT : 'transparent',
            border:
              selectedFunction === f.text ? `1px solid ${COLORS.JELLED_PRIMARY_BLUE}` : `1px solid ${COLORS.GRAY_5}`,
            '&:hover': {
              border: `1px solid ${COLORS.JELLED_PRIMARY_BLUE}`,
              background: COLORS.JELLED_MID_BLUE_10_PERCENT,
            },
          }}>
          {f.icon}
          <Typography variant="h5">{formatLabelForDisplay(capitalizeOnlyFirstLetter(f.text))}</Typography>
        </Stack>
      ))}
    </Stack>
  )
}

const PageOne = ({ setPage }: { setPage: React.Dispatch<React.SetStateAction<number>> }) => {
  return (
    <Stack justifyContent="center" alignItems="center">
      <Typography
        variant="h3"
        mb="14px"
        sx={{
          color: COLORS.GRAY_1,
        }}>
        Real quick — tell us a little more about you!
      </Typography>

      <Typography
        variant="pS"
        sx={{
          color: COLORS.GRAY_2,
        }}>
        This will help us suggest personalized topics in your insights.
      </Typography>

      <SmileyGraphic />

      <Button
        color="primary"
        variant="contained"
        size="medium"
        onClick={() => setPage(1)}
        sx={{
          width: 'fit-content',
        }}>
        Continue
      </Button>
    </Stack>
  )
}

const PageTwo = ({
  selectedRole,
  setPage,
  setSelectedRole,
}: {
  selectedRole: UserRole | null
  setPage: React.Dispatch<React.SetStateAction<number>>
  setSelectedRole: React.Dispatch<React.SetStateAction<UserRole | null>>
}) => {
  const onNextClick = async () => {
    setPage(2)
  }

  return (
    <Stack justifyContent="center" alignItems="center">
      <Typography variant="h3" mb={5}>
        How would you describe your role?
      </Typography>
      <UserRoleList selectedRole={selectedRole} setSelectedRole={setSelectedRole} />

      <Stack justifyContent="space-between" direction="row" width="100%" mt={4}>
        <Button variant="text" size="small" sx={{ width: '84px' }} onClick={() => setPage(0)}>
          Back
        </Button>

        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={!selectedRole}
          onClick={onNextClick}
          sx={{
            width: '84px',
          }}>
          Next
        </Button>
      </Stack>
    </Stack>
  )
}

const PageThree = ({
  customFunction,
  selectedFunction,
  setCustomFunction,
  setPage,
  setSelectedFunction,
}: {
  customFunction: string
  selectedFunction: string
  setCustomFunction: React.Dispatch<React.SetStateAction<string>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setSelectedFunction: React.Dispatch<React.SetStateAction<string>>
}) => {
  const isMobile = useCheckMobileScreen()

  const onCustomFunctionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFunction(event.target.value)
  }

  const onNextClick = () => {
    setPage(3)
  }

  const isNextDisabled = (selectedFunction === 'other' && !customFunction) || !selectedFunction

  return (
    <Stack justifyContent="center" width="608px" alignSelf="center">
      <Typography variant="h3" mb={5} textAlign="center">
        What kind of work do you do?
      </Typography>

      <FunctionsList
        selectedFunction={selectedFunction}
        setSelectedFunction={setSelectedFunction}
        setCustomFunction={setCustomFunction}
      />

      {selectedFunction === 'other' && (
        <TextField
          className="has-label"
          label="Other"
          placeholder="e.g. Design"
          value={customFunction}
          onChange={onCustomFunctionChange}
          sx={{
            width: isMobile ? '100%' : '400px',
            marginTop: '32px',
            alignSelf: 'left',
            marginBottom: '32px',
          }}
        />
      )}

      <Stack justifyContent="space-between" direction="row" width="100%" mt={4}>
        <Button variant="text" size="small" sx={{ width: '84px' }} onClick={() => setPage(1)}>
          Back
        </Button>

        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={isNextDisabled}
          onClick={onNextClick}
          sx={{
            width: '84px',
          }}>
          Next
        </Button>
      </Stack>
    </Stack>
  )
}

const PageFour = ({
  customFunction,
  onClose,
  selectedFunction,
  selectedRole,
  setPage,
}: {
  customFunction: string
  onClose: () => void
  selectedFunction: string
  selectedRole: UserRole | null
  setPage: React.Dispatch<React.SetStateAction<number>>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const [user, setUser] = useRecoilState(authenticatedUserAtom)
  const setToast = useSetRecoilState(toastAtom)
  const theme = useTheme()

  const myTwin = useRecoilValue(myTwinAtom)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [isApplyingTopicsAsLabels, setIsApplyingTopicsAsLabels] = useState<boolean>(false)
  const onCheckApplyingTopicsAsLabels = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsApplyingTopicsAsLabels(event.target.checked)
  }

  const { isLoading, suggestedCategories, setSuggestedCategories } = useFetchSuggestedCategories(myTwin?.id ?? '')
  const { isLoading: isLoadingTwinResponder, twinResponder } = useFetchTwinResponder(myTwin?.id ?? '')
  const { refetchInsightCategories } = useFetchInsightCategories(myTwin?.id ?? '')

  useEffect(() => {
    setIsApplyingTopicsAsLabels(twinResponder?.use_insight_topics_as_labels ?? false)
  }, [twinResponder])

  if (isLoading || isLoadingTwinResponder) {
    return (
      <CircularProgress
        size="24px"
        thickness={4}
        style={{
          color: COLORS.JELLED_PRIMARY_BLUE,
          alignSelf: 'center',
        }}
      />
    )
  }

  const onUpdateColor = (topicId: string, color: string) => {
    const updatedTopics = suggestedCategories.map((topic) => {
      if (topic.id === topicId) {
        return {
          ...topic,
          color,
        }
      }

      return topic
    })

    setSuggestedCategories(updatedTopics)
  }

  const onDoneClick = async () => {
    if (!activeOrganization || !myTwin?.id || !user) {
      return
    }

    setIsSubmitting(true)

    try {
      // first, we update the twin insight categories
      await twinsUpdateInsightCategories(activeOrganization, myTwin.id, suggestedCategories)

      // we need to update the user's role / function / onboarding steps completed
      const response = await usersUpdateAuthenticated({
        function: customFunction ? customFunction : selectedFunction,
        onboarding_steps_completed: [
          ...user.onboarding_steps_completed,
          OnboardingStep.topics_of_interest_update_finished,
        ],
        role: selectedRole ? selectedRole : undefined,
      })
      setUser(response.data)

      // we also need to update the flag for applying topics as labels in the twin responder
      await usersUpdateTwinResponder(activeOrganization, myTwin.id, {
        ...twinResponder,
        use_insight_topics_as_labels: isApplyingTopicsAsLabels,
      })

      // after updating the responder, we want to update the topics list by refetching insight categories
      refetchInsightCategories()

      setToast({
        title: 'Your preferences have been updated successfully',
        severity: 'success',
      })
      onClose()
    } catch (error) {
      setToast({
        title: 'Failed to update your preferences',
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Stack justifyContent="center" alignItems="center">
      <Typography variant="h3" mb={3} textAlign="center">
        Configure topics of interest
      </Typography>

      <Typography
        color="secondary"
        variant="pS"
        textAlign="center"
        sx={{
          color: COLORS.GRAY_2,
        }}>
        Here are some suggested topics that your insights from Gmail and Slack will be organized by. You can always add
        more topics later.
      </Typography>

      <Stack spacing="12px" my={5} width="420px">
        {suggestedCategories.map((topic, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TopicColorItem topic={topic} onUpdateColor={onUpdateColor} />

            <Typography variant="pXS" textAlign="left">
              {topic.name}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" alignItems="center">
        <CheckboxWithDarkMode
          disableRipple
          theme={theme}
          checked={isApplyingTopicsAsLabels}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCheckApplyingTopicsAsLabels(e)}
          color="primary"
          sx={{
            padding: 0,
            height: '42px',
            width: 'inherit',
          }}
        />
        <Typography variant="pS">Apply topics as labels in Gmail to organize incoming emails</Typography>
      </Stack>

      <Stack justifyContent="space-between" direction="row" width="100%" mt={4}>
        <Button variant="text" size="small" sx={{ width: '84px' }} onClick={() => setPage(2)}>
          Back
        </Button>

        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={isSubmitting}
          onClick={onDoneClick}
          sx={{
            width: '84px',
          }}>
          Done
        </Button>
      </Stack>
    </Stack>
  )
}

const UserInfoModal = ({ onClose }: { onClose: () => void }) => {
  const isMobile = useCheckMobileScreen()

  const user = useRecoilValue(authenticatedUserAtom)

  const [page, setPage] = useState<number>(0)

  // local state for the user role / function
  const [customFunction, setCustomFunction] = useState<string>('')
  const [selectedFunction, setSelectedFunction] = useState<string>(user?.function ?? '')
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(user?.role ?? null)

  useEffect(() => {
    if (!user) {
      return
    }

    const isCustomFunction = !FUNCTIONS.find((f) => f.text === user.function)

    if (isCustomFunction && user.function) {
      setSelectedFunction('other')
      setCustomFunction(user.function)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderPage = () => {
    switch (page) {
      case 0:
        return <PageOne setPage={setPage} />
      case 1:
        return <PageTwo selectedRole={selectedRole} setSelectedRole={setSelectedRole} setPage={setPage} />
      case 2:
        return (
          <PageThree
            selectedFunction={selectedFunction}
            setSelectedFunction={setSelectedFunction}
            customFunction={customFunction}
            setCustomFunction={setCustomFunction}
            setPage={setPage}
          />
        )
      case 3:
        return (
          <PageFour
            selectedFunction={selectedFunction}
            selectedRole={selectedRole}
            customFunction={customFunction}
            setPage={setPage}
            onClose={onClose}
          />
        )
    }
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      width={isMobile ? '100%' : '704px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
        paddingTop: '60px',
        paddingBottom: '40px',
      }}>
      {renderPage()}
    </JelledModal>
  )
}

export default UserInfoModal
